import { useEffect, useRef } from "react";

import type { CheckoutGTMFlowType, TemplateName, TrackingEventsAdditionalData } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useCheckoutFastFlowContext } from "@checkout-v2/features/checkoutFastFlow";
import type { CartData } from "@checkout-v2/shared/utils";
import { buildCommerceBaseData } from "@checkout-v2/shared/utils";

import { useCartItems } from "../useCartItems";
import { useCartPrices } from "../useCartPrices";

const CHECKOUT_TEMPLATE_NAME: TemplateName = "checkout";

const dispatchGtmCheckoutBegin = (cart: CartData, checkoutFlowType: CheckoutGTMFlowType): void => {
  const data: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutBeginCheckout] = {
    section: CHECKOUT_TEMPLATE_NAME,
    template_name: CHECKOUT_TEMPLATE_NAME,
    ...buildCommerceBaseData(cart),
    checkout_flow_type: checkoutFlowType,
  };

  dispatchTrackingEvent(TrackingEvents.TrackingCheckoutBeginCheckout, data);
};

export const useGtmCheckoutBegin = (): void => {
  const { checkoutFlowType } = useCheckoutFastFlowContext();
  const { data: cartItems, isLoading: isLoadingCartItems } = useCartItems();
  const { data: cartPrices, isLoading: isLoadingCartPrices } = useCartPrices();

  const hasDispatchedCheckoutBeginRef = useRef(false);

  useEffect(() => {
    if (hasDispatchedCheckoutBeginRef.current) {
      return;
    }

    if (!checkoutFlowType) {
      return;
    }

    if (isLoadingCartItems || isLoadingCartPrices) {
      return;
    }

    if (!cartItems || !cartPrices) {
      return;
    }

    hasDispatchedCheckoutBeginRef.current = true;

    dispatchGtmCheckoutBegin({ items: cartItems, prices: cartPrices }, checkoutFlowType);
  }, [cartItems, cartPrices, isLoadingCartItems, isLoadingCartPrices, checkoutFlowType]);
};
